import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ServiceItem {
  icon: string;
  title: string;
  color: string;
  image: string;
  desc: string;
  benefits: string[];
  forWho: string;
}

const ServiceCard = ({ service, index, t }: { service: ServiceItem; index: number; t: any }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-center transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Image */}
      <div className="w-full lg:w-[45%] flex-shrink-0">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-56 md:h-64 object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030006]/70 via-transparent to-transparent" />
          <div
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ background: `${service.color}25`, border: `1px solid ${service.color}40`, color: service.color }}
          >
            <i className={`${service.icon} text-sm`} />
            {service.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
          style={{ background: `${service.color}15`, border: `1px solid ${service.color}30` }}
        >
          <i className={`${service.icon} text-xl`} style={{ color: service.color }} />
        </div>

        <h2 className="text-xl md:text-2xl font-black text-white mb-3">{service.title}</h2>
        <p className="text-white/55 text-sm leading-relaxed mb-5">{service.desc}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          {service.benefits.map((b) => (
            <div key={b} className="flex items-start gap-2">
              <div
                className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                style={{ background: `${service.color}20` }}
              >
                <i className="ri-check-line text-[10px]" style={{ color: service.color }} />
              </div>
              <span className="text-white/55 text-xs leading-relaxed">{b}</span>
            </div>
          ))}
        </div>

        <div className="bg-white/3 border border-white/8 rounded-xl p-3.5 mb-5">
          <span className="text-white/40 text-xs uppercase tracking-wider font-bold">{t('services.detail.common.forWho')} </span>
          <span className="text-white/55 text-xs leading-relaxed">{service.forWho}</span>
        </div>

        <Link
          to="/contact"
          className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
          style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
        >
          <i className="ri-calendar-line" />
          {t('services.detail.common.book')}
        </Link>
      </div>
    </div>
  );
};

const ServicesDetail = () => {
  const { t } = useTranslation();
  const featuredRef = useRef<HTMLDivElement>(null);
  const [featuredVisible, setFeaturedVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setFeaturedVisible(true); },
      { threshold: 0.1 }
    );
    if (featuredRef.current) observer.observe(featuredRef.current);
    return () => observer.disconnect();
  }, []);

  const featuredService = {
    icon: 'ri-global-line',
    title: t('services.detail.featured.title'),
    color: '#00c2ff',
    accentColor: '#8c52ff',
    image: 'https://readdy.ai/api/search-image?query=modern%20professional%20website%20design%20laptop%20screen%20glowing%20neon%20light%20dark%20background%20custom%20web%20development%20high%20converting%20landing%20page%20clean%20minimal%20elegant%20digital%20agency%20studio&width=1200&height=600&seq=feat1&orientation=landscape',
    desc: t('services.detail.featured.desc'),
    benefits: [
      t('services.detail.featured.benefits.b1'),
      t('services.detail.featured.benefits.b2'),
      t('services.detail.featured.benefits.b3'),
      t('services.detail.featured.benefits.b4'),
      t('services.detail.featured.benefits.b5'),
      t('services.detail.featured.benefits.b6'),
    ],
    forWho: t('services.detail.featured.forWho'),
  };

  const services: ServiceItem[] = [
    {
      icon: 'ri-customer-service-2-line',
      title: t('services.detail.items.crm.title'),
      color: '#FF2DAA',
      image: 'https://readdy.ai/api/search-image?query=CRM%20dashboard%20client%20management%20system%20modern%20dark%20UI%20neon%20pink%20purple%20glow%20leads%20pipeline%20workflow%20automation%20professional%20software%20interface&width=700&height=450&seq=crm1&orientation=landscape',
      desc: t('services.detail.items.crm.desc'),
      benefits: [
        t('services.detail.items.crm.benefits.b1'),
        t('services.detail.items.crm.benefits.b2'),
        t('services.detail.items.crm.benefits.b3'),
        t('services.detail.items.crm.benefits.b4'),
      ],
      forWho: t('services.detail.items.crm.forWho'),
    },
    {
      icon: 'ri-palette-line',
      title: t('services.detail.items.branding.title'),
      color: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=brand%20identity%20design%20logo%20color%20palette%20typography%20visual%20direction%20modern%20elegant%20dark%20background%20purple%20gradient%20creative%20agency%20professional%20branding%20kit&width=700&height=450&seq=brand1&orientation=landscape',
      desc: t('services.detail.items.branding.desc'),
      benefits: [
        t('services.detail.items.branding.benefits.b1'),
        t('services.detail.items.branding.benefits.b2'),
        t('services.detail.items.branding.benefits.b3'),
        t('services.detail.items.branding.benefits.b4'),
      ],
      forWho: t('services.detail.items.branding.forWho'),
    },
    {
      icon: 'ri-instagram-line',
      title: t('services.detail.items.consulting.title'),
      color: '#FF2DAA',
      image: 'https://readdy.ai/api/search-image?query=social%20media%20strategy%20consulting%20session%20digital%20marketing%20growth%20analytics%20instagram%20tiktok%20dark%20background%20neon%20glow%20professional%20entrepreneur%20business&width=700&height=450&seq=smc1&orientation=landscape',
      desc: t('services.detail.items.consulting.desc'),
      benefits: [
        t('services.detail.items.consulting.benefits.b1'),
        t('services.detail.items.consulting.benefits.b2'),
        t('services.detail.items.consulting.benefits.b3'),
        t('services.detail.items.consulting.benefits.b4'),
      ],
      forWho: t('services.detail.items.consulting.forWho'),
    },
    {
      icon: 'ri-video-line',
      title: t('services.detail.items.content.title'),
      color: '#00c2ff',
      image: 'https://readdy.ai/api/search-image?query=content%20creation%20consulting%20video%20strategy%20creative%20direction%20dark%20studio%20neon%20blue%20glow%20camera%20equipment%20professional%20creator%20business%20coaching&width=700&height=450&seq=ccc1&orientation=landscape',
      desc: t('services.detail.items.content.desc'),
      benefits: [
        t('services.detail.items.content.benefits.b1'),
        t('services.detail.items.content.benefits.b2'),
        t('services.detail.items.content.benefits.b3'),
        t('services.detail.items.content.benefits.b4'),
      ],
      forWho: t('services.detail.items.content.forWho'),
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: t('services.detail.items.optimization.title'),
      color: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=social%20media%20profile%20optimization%20analytics%20dashboard%20dark%20background%20purple%20neon%20glow%20business%20branding%20conversion%20improvement%20professional%20digital%20marketing&width=700&height=450&seq=smo1&orientation=landscape',
      desc: t('services.detail.items.optimization.desc'),
      benefits: [
        t('services.detail.items.optimization.benefits.b1'),
        t('services.detail.items.optimization.benefits.b2'),
        t('services.detail.items.optimization.benefits.b3'),
        t('services.detail.items.optimization.benefits.b4'),
      ],
      forWho: t('services.detail.items.optimization.forWho'),
    },
    {
      icon: 'ri-map-pin-2-line',
      title: t('services.detail.items.google.title'),
      color: '#FF2DAA',
      image: 'https://readdy.ai/api/search-image?query=Google%20Business%20Profile%20local%20SEO%20map%20listing%20setup%20dark%20background%20neon%20pink%20glow%20local%20business%20visibility%20search%20results%20professional%20digital%20marketing&width=700&height=450&seq=gbp1&orientation=landscape',
      desc: t('services.detail.items.google.desc'),
      benefits: [
        t('services.detail.items.google.benefits.b1'),
        t('services.detail.items.google.benefits.b2'),
        t('services.detail.items.google.benefits.b3'),
        t('services.detail.items.google.benefits.b4'),
      ],
      forWho: t('services.detail.items.google.forWho'),
    },
    {
      icon: 'ri-robot-line',
      title: t('services.detail.items.ai.title'),
      color: '#00c2ff',
      image: 'https://readdy.ai/api/search-image?query=AI%20automation%20sales%20funnel%20digital%20workflow%20robot%20technology%20dark%20background%20neon%20blue%20glow%20futuristic%20business%20automation%20modern%20systems%20leads%20conversion&width=700&height=450&seq=ai1&orientation=landscape',
      desc: t('services.detail.items.ai.desc'),
      benefits: [
        t('services.detail.items.ai.benefits.b1'),
        t('services.detail.items.ai.benefits.b2'),
        t('services.detail.items.ai.benefits.b3'),
        t('services.detail.items.ai.benefits.b4'),
      ],
      forWho: t('services.detail.items.ai.forWho'),
    },
    {
      icon: 'ri-lightbulb-line',
      title: t('services.detail.items.strategy.title'),
      color: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=business%20consulting%20strategy%20session%20entrepreneur%20coaching%20dark%20background%20purple%20neon%20glow%20whiteboard%20planning%20digital%20growth%20roadmap%20professional%20advisor&width=700&height=450&seq=cons1&orientation=landscape',
      desc: t('services.detail.items.strategy.desc'),
      benefits: [
        t('services.detail.items.strategy.benefits.b1'),
        t('services.detail.items.strategy.benefits.b2'),
        t('services.detail.items.strategy.benefits.b3'),
        t('services.detail.items.strategy.benefits.b4'),
      ],
      forWho: t('services.detail.items.strategy.forWho'),
    },
  ];

  return (
    <section className="relative py-24 bg-[#030006] overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse,rgba(140,82,255,0.06)_0%,transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* ── FEATURED SERVICE ── */}
        <div className="mb-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#00c2ff]/10 border border-[#00c2ff]/30 rounded-full px-4 py-1.5 text-[#00c2ff] text-xs font-bold uppercase tracking-widest">
            <i className="ri-star-line" />
            {t('services.detail.featured.badge')}
          </span>
        </div>

        <div
          ref={featuredRef}
          className={`relative rounded-3xl overflow-hidden border border-[#00c2ff]/20 mb-28 transition-all duration-1000 ${
            featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ background: 'linear-gradient(135deg, rgba(0,194,255,0.06) 0%, rgba(140,82,255,0.06) 100%)' }}
        >
          {/* Top glow line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00c2ff]/60 to-transparent" />

          <div className="flex flex-col lg:flex-row">
            {/* Image side */}
            <div className="w-full lg:w-1/2 relative overflow-hidden">
              <img
                src={featuredService.image}
                alt={featuredService.title}
                className="w-full h-72 lg:h-full min-h-[400px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#030006] hidden lg:block" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030006]/80 to-transparent lg:hidden" />
              {/* Badge */}
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-[#00c2ff]/20 border border-[#00c2ff]/40 rounded-full px-4 py-2">
                <div className="w-2 h-2 rounded-full bg-[#00c2ff] animate-pulse" />
                <span className="text-[#00c2ff] text-xs font-bold uppercase tracking-wider">{t('services.detail.featured.mostPopular')}</span>
              </div>
            </div>

            {/* Content side */}
            <div className="flex-1 p-8 md:p-12 flex flex-col justify-center">
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
                style={{ background: 'rgba(0,194,255,0.15)', border: '1px solid rgba(0,194,255,0.3)' }}
              >
                <i className={`${featuredService.icon} text-2xl text-[#00c2ff]`} />
              </div>

              <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
                {featuredService.title}
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-7">{featuredService.desc}</p>

              {/* Benefits grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                {featuredService.benefits.map((b) => (
                  <div key={b} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 bg-[#00c2ff]/15">
                      <i className="ri-check-line text-xs text-[#00c2ff]" />
                    </div>
                    <span className="text-white/65 text-sm leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>

              {/* For who */}
              <div className="bg-white/4 border border-white/10 rounded-xl p-4 mb-7">
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-user-heart-line text-[#00c2ff] text-sm" />
                  <span className="text-white/50 text-xs uppercase tracking-wider font-bold">{t('services.detail.common.forWho')}</span>
                </div>
                <p className="text-white/60 text-sm leading-relaxed">{featuredService.forWho}</p>
              </div>

              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-base w-fit"
                style={{ background: 'linear-gradient(135deg, #00c2ff, #8c52ff)' }}
              >
                <i className="ri-calendar-check-line" />
                {t('services.detail.common.book')}
              </Link>
            </div>
          </div>
        </div>

        {/* ── SECTION HEADER ── */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            {t('services.detail.header.prefix')} <span className="bg-gradient-to-r from-[#FF2DAA] to-[#8c52ff] bg-clip-text text-transparent">{t('services.detail.header.highlight')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('services.detail.header.subtitle')}
          </p>
        </div>

        {/* ── SERVICE CARDS ── */}
        <div className="space-y-20">
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} t={t} />
          ))}
        </div>

        {/* ── BOTTOM CTA STRIP ── */}
        <div className="mt-24 relative rounded-3xl overflow-hidden border border-white/10 p-10 md:p-14 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(140,82,255,0.08) 0%, rgba(0,194,255,0.08) 100%)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8c52ff]/50 to-transparent" />
          <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{t('services.detail.footerCta.title')}</h3>
          <p className="text-white/55 text-base mb-8 max-w-lg mx-auto">
            {t('services.detail.footerCta.subtitle')}
          </p>
          <Link
            to="/contact"
            className="whitespace-nowrap inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 cursor-pointer text-base"
          >
            <i className="ri-chat-1-line" />
            {t('services.detail.footerCta.book')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesDetail;
